/**
 * Pure-function validation gate for Web Share Target payloads.
 *
 * Web equivalent of `kaether_for_ios/Stoat/E2EE/ShareExtensionPolicy.swift`.
 *
 * THREAT MODEL
 *   The Service Worker `fetch` handler intercepts the share-target POST,
 *   parses the multipart `FormData` into `ShareItem`s, and runs this
 *   validation BEFORE persisting anything to IndexedDB and BEFORE
 *   redirecting to the composer. Two design rules:
 *
 *   1. The Web Share Target is NOT a back door around `AttachmentPolicy`.
 *      Per-item byte caps mirror `AttachmentPolicy.maxBytes(for:)` so a
 *      hostile page cannot inflate an attachment past the in-app composer's
 *      gate by routing it through the share sheet.
 *   2. The SW handler never talks to the network on validation. The
 *      payload is read directly from the request `FormData`; there is no
 *      validate-by-URL path. `ShareItem` itself has no `remoteURL` field.
 *
 *   Validation is a pure function of (items, caption). It returns a
 *   closed-discriminated `ValidationDecision` so the SW handler can switch
 *   on the error kind and pick a stable redirect target / status code.
 *
 *   Decision ordering inside `validateSharedPayload`:
 *     1. items.length === 0                  → noItemsProvided
 *     2. items.length > MAX_ITEMS_PER_SHARE  → tooManyItems
 *     3. captionScalarCount > 1500           → captionTooLong
 *     4. for each item:
 *          a. mimeType outside allow-list    → invalidMimeType
 *          b. plaintext invariant violated   → unsupportedKind
 *          c. byteSize > category cap        → oversizeAttachment
 *     5. totalBytes > MAX_TOTAL_PAYLOAD_BYTES → totalPayloadOverLimit
 *
 *   Step 5 runs LAST because the per-item check has already proven each
 *   item respects its own category cap; the total-payload check is the
 *   stricter cross-item budget protecting the SW process memory.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs. Pure logic.
 */

import {
  unicodeScalarCount,
} from "./ShareTargetPayload";
import type { ShareItem, SharePayloadError } from "./ShareTargetPayload";

// ---------------------------------------------------------------------------
// Caps — mirror iOS exactly
// ---------------------------------------------------------------------------

/** Total payload byte cap across all attachments. Mirrors iOS. */
export const MAX_TOTAL_PAYLOAD_BYTES = 50 * 1024 * 1024;

/** Item count cap per share. Mirrors iOS Share Extension. */
export const MAX_ITEMS_PER_SHARE = 10;

/** Caption length cap counted in unicode scalars (NOT UTF-16 code units). */
export const MAX_CAPTION_UNICODE_SCALARS = 1500;

/**
 * MIME allow-list mirroring `AttachmentMimeType` in
 * `attachment-policy/AttachmentPolicy.ts`. The SW target is leaner than the
 * main composer; the main composer re-validates the dequeued draft against
 * `AttachmentPolicy.decide` proper (including the magic-byte sniff) before
 * the encrypt step.
 *
 * Keep in sync with `AttachmentPolicy.ts:ALLOWED_MIME_TYPES`.
 */
const ALLOWED_MIME_TYPES: ReadonlySet<string> = new Set<string>([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "video/mp4",
  "video/quicktime",
  "audio/mp4",
  "audio/aac",
  "audio/opus",
  "application/pdf",
  "text/plain",
  "application/octet-stream",
]);

// ---------------------------------------------------------------------------
// Per-category byte cap
// ---------------------------------------------------------------------------

const IMAGE_MAX = 25 * 1024 * 1024;
const VIDEO_MAX = 250 * 1024 * 1024;
const VOICE_MAX = 3 * 1024 * 1024;
const AUDIO_MAX = 50 * 1024 * 1024;
const PDF_MAX = 100 * 1024 * 1024;
const GENERIC_MAX = 25 * 1024 * 1024;

/**
 * Per-category byte cap, mirroring `AttachmentSizeBudget.maxBytes(...)`.
 * We hand-roll the table here because the SW target is leaner than the
 * main composer; the main composer re-validates the dequeued draft against
 * `AttachmentPolicy` proper before the encrypt step.
 */
function categoryByteCap(item: ShareItem): number {
  switch (item.kind) {
    case "image":
      return IMAGE_MAX;
    case "video":
      return VIDEO_MAX;
    case "file":
      switch (item.mimeType) {
        case "audio/opus":
          return VOICE_MAX;
        case "audio/mp4":
        case "audio/aac":
          return AUDIO_MAX;
        case "application/pdf":
          return PDF_MAX;
        default:
          return GENERIC_MAX;
      }
    case "text":
    case "url":
      // String-bearing kinds aren't sized by the byte cap; their size
      // budget is the unicode-scalar caption cap (text and URL are short
      // by construction).
      return GENERIC_MAX;
  }
}

// ---------------------------------------------------------------------------
// Validation decision (closed discriminated union)
// ---------------------------------------------------------------------------

export type ValidationDecision =
  | { readonly kind: "allow" }
  | { readonly kind: "reject"; readonly error: SharePayloadError };

const ALLOW: ValidationDecision = { kind: "allow" };

function reject(error: SharePayloadError): ValidationDecision {
  return { kind: "reject", error };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Pure validation of a share-target payload. See file header for decision
 * ordering. Returns `{ kind: 'allow' }` or `{ kind: 'reject', error }`.
 *
 * `caption` is normalised by the caller; we count unicode scalars (NOT
 * UTF-16 code units) so emoji-heavy captions can't sneak past a UTF-16
 * length check.
 */
export function validateSharedPayload(
  items: readonly ShareItem[],
  caption: string | undefined,
): ValidationDecision {
  if (items.length === 0) {
    return reject({ kind: "noItemsProvided" });
  }

  if (items.length > MAX_ITEMS_PER_SHARE) {
    return reject({ kind: "tooManyItems", count: items.length });
  }

  if (caption !== undefined) {
    const scalarCount = unicodeScalarCount(caption);
    if (scalarCount > MAX_CAPTION_UNICODE_SCALARS) {
      return reject({ kind: "captionTooLong", scalarCount });
    }
  }

  let totalBytes = 0;
  for (const item of items) {
    // MIME allow-list. Undefined mimeType is OK only for `text` and `url`
    // (host apps don't always set it for these). For bytes-bearing kinds
    // we require an explicit MIME so the main composer can dispatch
    // through `AttachmentMimeType`.
    if (item.mimeType !== undefined) {
      if (!ALLOWED_MIME_TYPES.has(item.mimeType)) {
        return reject({ kind: "invalidMimeType", mime: item.mimeType });
      }
    } else {
      switch (item.kind) {
        case "text":
        case "url":
          break; // mimeType is optional for string-bearing kinds
        case "image":
        case "video":
        case "file":
          return reject({ kind: "invalidMimeType", mime: "<missing>" });
      }
    }

    // Plaintext invariant: exactly one of bytes / string set, matching the
    // kind. Anything else is a malformed item.
    switch (item.kind) {
      case "text":
      case "url":
        if (
          item.plaintextString === undefined ||
          item.plaintextBytes !== undefined
        ) {
          return reject({
            kind: "unsupportedKind",
            detail: `kind=${item.kind} requires plaintextString and forbids plaintextBytes`,
          });
        }
        break;
      case "image":
      case "video":
      case "file":
        if (
          item.plaintextBytes === undefined ||
          item.plaintextString !== undefined
        ) {
          return reject({
            kind: "unsupportedKind",
            detail: `kind=${item.kind} requires plaintextBytes and forbids plaintextString`,
          });
        }
        break;
    }

    // Per-item size against the category cap. Effective cap is the smaller
    // of (category cap, total payload budget) — even an in-spec image
    // can't be 50 MB if its category cap is 25 MB, and even a video can't
    // be 250 MB if the total cap is 50 MB.
    const itemBytes = item.plaintextBytes?.byteLength ?? 0;
    if (itemBytes > 0) {
      const cap = Math.min(categoryByteCap(item), MAX_TOTAL_PAYLOAD_BYTES);
      if (itemBytes > cap) {
        return reject({ kind: "oversizeAttachment", byteSize: itemBytes });
      }
    }
    totalBytes += itemBytes;
  }

  if (totalBytes > MAX_TOTAL_PAYLOAD_BYTES) {
    return reject({ kind: "totalPayloadOverLimit", totalBytes });
  }

  return ALLOW;
}
